import { c as _c } from "react/compiler-runtime";
import { DocType } from '@assets/icons';
import { cn } from 'cn';
import { formatDistanceToNowStrict, isValid } from 'date-fns';
import { Button } from '@/components/ui/button';
import type { ImportQueueItem } from '../types/import-queue-item';
import type { ImportQueueStatus } from '../types/import-queue-status';
import { ImportStatusIndicator } from './import-status-indicator';
const DEFAULT_STATUS_LABEL: Record<ImportQueueStatus, string> = {
  queued: 'Queued',
  preparing: 'Preparing',
  uploading: 'Uploading',
  processing: 'Processing',
  done: 'Complete',
  failed: 'Failed'
};
interface ImportQueueRowProps {
  item: ImportQueueItem;
  depth: number;
  isActive: boolean;
  statusLabelOverride?: Partial<Record<ImportQueueStatus, string>>;
  onClick?: () => void;
  onRetry: (clientId: string) => void;
}
export function ImportQueueRow(t0) {
  const $ = _c(53);
  const {
    item,
    depth,
    isActive,
    statusLabelOverride,
    onClick,
    onRetry
  } = t0;
  const indent = depth * 12;
  const statusLabel = statusLabelOverride?.[item.status] ?? DEFAULT_STATUS_LABEL[item.status];
  const navigable = Boolean(item.navigationId) && Boolean(onClick) && item.status !== "failed";
  let t1;
  if ($[0] !== item.uploadedAt) {
    const uploadedDate = item.uploadedAt ? new Date(item.uploadedAt) : null;
    t1 = uploadedDate && isValid(uploadedDate) ? `Uploaded ${formatDistanceToNowStrict(uploadedDate, {
      addSuffix: true
    })}` : null;
    $[0] = item.uploadedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const uploadedLabel = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DocType className="text-muted-foreground size-8 shrink-0" aria-hidden={true} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== item.fileName) {
    t3 = <p className="text-foreground truncate text-sm leading-tight font-medium" title={item.fileName}>{item.fileName}</p>;
    $[3] = item.fileName;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = item.status === "failed";
  const t5 = item.status !== "failed";
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = cn("mt-1 flex items-center gap-1.5 text-xs", {
      "text-destructive": t4,
      "text-muted-foreground": t5
    });
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== item.status) {
    t7 = <ImportStatusIndicator status={item.status} />;
    $[8] = item.status;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = item.detail ? `${statusLabel} · ${item.detail}` : statusLabel;
  let t9;
  if ($[10] !== t8) {
    t9 = <span className="truncate">{t8}</span>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== t9) {
    t10 = <div className={t6}>{t7}{t9}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== item.errorMessage || $[17] !== item.status) {
    t11 = item.status === "failed" && item.errorMessage ? <p className="text-destructive mt-2 text-xs leading-snug">{item.errorMessage}</p> : null;
    $[16] = item.errorMessage;
    $[17] = item.status;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== uploadedLabel) {
    t12 = uploadedLabel ? <p className="text-muted-foreground mt-1 text-xs leading-snug">{uploadedLabel}</p> : null;
    $[19] = uploadedLabel;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== item.retryDisabledReason || $[22] !== item.status) {
    t13 = item.status === "failed" && item.retryDisabledReason ? <p className="text-muted-foreground mt-2 text-xs leading-snug">{item.retryDisabledReason}</p> : null;
    $[21] = item.retryDisabledReason;
    $[22] = item.status;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== item.clientId || $[25] !== item.retryDisabledReason || $[26] !== item.status || $[27] !== onRetry) {
    t14 = item.status === "failed" && !item.retryDisabledReason ? <Button type="button" variant="secondary" size="sm" className="mt-2 h-8 text-xs" onClick={e => {
      e.stopPropagation();
      onRetry(item.clientId);
    }}>Retry</Button> : null;
    $[24] = item.clientId;
    $[25] = item.retryDisabledReason;
    $[26] = item.status;
    $[27] = onRetry;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== t3) {
    t15 = <>{t2}<div className="min-w-0 flex-1">{t3}{t10}{t11}{t12}{t13}{t14}</div></>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t3;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const content = t15;
  if (navigable) {
    const t16 = `${indent + 12}px`;
    let t17;
    if ($[36] !== t16) {
      t17 = {
        paddingLeft: t16
      };
      $[36] = t16;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    let t18;
    if ($[38] !== isActive) {
      t18 = cn("hover:bg-accent flex w-full items-start gap-3 rounded-lg border border-transparent py-2.5 pr-3 text-left transition-colors", {
        "border-primary/30 bg-accent ring-ring/20 ring-1": isActive
      });
      $[38] = isActive;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    let t19;
    if ($[40] !== content || $[41] !== onClick || $[42] !== t17 || $[43] !== t18) {
      t19 = <button type="button" onClick={onClick} style={t17} className={t18}>{content}</button>;
      $[40] = content;
      $[41] = onClick;
      $[42] = t17;
      $[43] = t18;
      $[44] = t19;
    } else {
      t19 = $[44];
    }
    return t19;
  }
  const t16 = `${indent + 12}px`;
  let t17;
  if ($[45] !== t16) {
    t17 = {
      paddingLeft: t16
    };
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const t18 = item.status === "failed";
  let t19;
  if ($[47] !== t18) {
    t19 = cn("border-border flex items-start gap-3 rounded-lg border py-2.5 pr-3", {
      "border-destructive/30": t18
    });
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== content || $[50] !== t17 || $[51] !== t19) {
    t20 = <div style={t17} className={t19} data-sentry-component="ImportQueueRow" data-sentry-source-file="import-queue-row.tsx">{content}</div>;
    $[49] = content;
    $[50] = t17;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
}